import axios from 'axios';
import dayjs from 'dayjs';
import { formatNumber } from './helper';

const API_BASE_URL = 'https://api.uptimerobot.com/v3';
const MAX_HISTORY_DAYS = 90;
const CACHE_DURATION = 5 * 60 * 1000;

function getHeaders(apiToken) {
  return {
    Authorization: `Bearer ${apiToken}`,
    Accept: 'application/json',
  };
}

function resolveNextLink(nextLink) {
  if (!nextLink) return null;

  const url = new URL(nextLink, API_BASE_URL);
  if (url.origin !== new URL(API_BASE_URL).origin || !url.pathname.startsWith('/v3/')) {
    throw new Error('UptimeRobot returned an invalid pagination link');
  }

  return url.toString();
}

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

async function getWithRateLimitRetry(url, options, retries = 1) {
  try {
    return await axios.get(url, options);
  }
  catch (error) {
    if (error.response?.status !== 429 || retries < 1) throw error;

    const retryAfter = Number(error.response.headers?.['retry-after']);
    const delay = Number.isFinite(retryAfter) ? retryAfter * 1000 : 60000;
    await wait(Math.max(1000, Math.min(delay, 60000)));
    return getWithRateLimitRetry(url, options, retries - 1);
  }
}

async function getAllPages(path, apiToken, params) {
  const items = [];
  const visited = new Set();
  let url = `${API_BASE_URL}${path}`;
  let query = params;

  while (url) {
    if (visited.has(url)) throw new Error('UptimeRobot returned a repeated pagination link');
    visited.add(url);

    const response = await getWithRateLimitRetry(url, {
      headers: getHeaders(apiToken),
      params: query,
      timeout: 10000,
    });

    if (!Array.isArray(response.data?.data)) {
      throw new Error('UptimeRobot returned an invalid response');
    }

    items.push(...response.data.data);
    url = resolveNextLink(response.data.nextLink);
    query = undefined;
  }

  return items;
}

function parseDate(value) {
  if (typeof value === 'number') {
    return value > 1000000000000 ? dayjs(value) : dayjs.unix(value);
  }
  return dayjs(value);
}

function getIncidentEnd(incident, now) {
  if (incident.resolvedAt) return parseDate(incident.resolvedAt);
  if (incident.duration !== null && incident.duration !== undefined) {
    return parseDate(incident.startedAt).add(incident.duration, 'second');
  }
  return now;
}

function overlapSeconds(startA, endA, startB, endB) {
  const start = Math.max(startA.valueOf(), startB.valueOf());
  const end = Math.min(endA.valueOf(), endB.valueOf());
  return Math.max(0, (end - start) / 1000);
}

function buildDailyStats(dates, monitor, incidents, now) {
  const monitorCreatedAt = parseDate(monitor.createDateTime);

  return dates.map((date) => {
    const dayEnd = date.add(1, 'day');
    const observedStart = monitorCreatedAt.isValid() && monitorCreatedAt.isAfter(date)
      ? monitorCreatedAt
      : date;
    const observedEnd = now.isBefore(dayEnd) ? now : dayEnd;
    const observedSeconds = Math.max(0, observedEnd.diff(observedStart, 'second'));

    const down = incidents.reduce((result, incident) => {
      const incidentStart = parseDate(incident.startedAt);
      if (!incidentStart.isValid()) return result;

      const incidentEnd = getIncidentEnd(incident, now);
      result.duration += overlapSeconds(observedStart, observedEnd, incidentStart, incidentEnd);
      if (!incidentStart.isBefore(date) && incidentStart.isBefore(dayEnd)) result.times += 1;
      return result;
    }, { times: 0, duration: 0 });

    const uptime = observedSeconds > 0
      ? Math.max(0, ((observedSeconds - down.duration) / observedSeconds) * 100)
      : 0;

    return {
      date,
      uptime: formatNumber(uptime),
      down,
    };
  });
}

function getStatus(status) {
  if (status === 'UP') return 'ok';
  if (status === 'DOWN' || status === 'LOOKS_DOWN') return 'down';
  return 'unknow';
}

function getLocalSummary(monitor, incidents, from, to, daily) {
  const monitorCreatedAt = parseDate(monitor.createDateTime);
  const observedStart = monitorCreatedAt.isValid() && monitorCreatedAt.isAfter(from)
    ? monitorCreatedAt
    : from;
  const observedSeconds = Math.max(0, to.diff(observedStart, 'second'));
  const downtime = daily.reduce((total, date) => total + date.down.duration, 0);

  return {
    uptime: observedSeconds > 0
      ? Math.max(0, ((observedSeconds - downtime) / observedSeconds) * 100)
      : 0,
    incident_count: incidents.length,
    total_downtime_seconds: downtime,
  };
}

function getCacheKey(apiToken, days) {
  return `uptimerobot-v3:${apiToken.slice(-12)}:${days}`;
}

function readCache(apiToken, days) {
  try {
    const cached = JSON.parse(window.localStorage.getItem(getCacheKey(apiToken, days)));
    if (!cached || Date.now() - cached.createdAt > CACHE_DURATION) return null;
    return cached.monitors.map((monitor) => ({
      ...monitor,
      daily: monitor.daily.map((date) => ({ ...date, date: dayjs(date.date) })),
    }));
  }
  catch (error) {
    return null;
  }
}

function writeCache(apiToken, days, monitors) {
  try {
    window.localStorage.setItem(getCacheKey(apiToken, days), JSON.stringify({
      createdAt: Date.now(),
      monitors,
    }));
  }
  catch (error) {
    // The status page still works when storage is blocked or full.
  }
}

export async function GetMonitors(apiToken, days) {
  const historyDays = Number(days);
  if (!apiToken) throw new Error('请配置 UptimeRobot API v3 Read-Only Token');
  if (!Number.isInteger(historyDays) || historyDays < 1 || historyDays > MAX_HISTORY_DAYS) {
    throw new Error(`CountDays 必须是 1 到 ${MAX_HISTORY_DAYS} 之间的整数`);
  }

  const cached = readCache(apiToken, historyDays);
  if (cached) return cached;

  const now = dayjs();
  const today = now.startOf('day');
  const dates = [];
  for (let d = 0; d < historyDays; d++) dates.push(today.subtract(d, 'day'));

  const from = dates[dates.length - 1];
  const to = now;
  const [monitors, incidents] = await Promise.all([
    getAllPages('/monitors', apiToken, { limit: 200 }),
    getAllPages('/incidents', apiToken, {
      started_after: from.toISOString(),
      started_before: to.toISOString(),
    }),
  ]);

  const incidentsByMonitor = incidents.reduce((result, incident) => {
    if (incident.includeInReports === false || !incident.monitor?.id) return result;
    const monitorId = String(incident.monitor.id);
    if (!result[monitorId]) result[monitorId] = [];
    result[monitorId].push(incident);
    return result;
  }, {});

  const result = monitors.map((monitor) => {
    const monitorIncidents = incidentsByMonitor[String(monitor.id)] || [];
    const daily = buildDailyStats(dates, monitor, monitorIncidents, now);
    const stats = getLocalSummary(monitor, monitorIncidents, from, to, daily);

    return {
      id: monitor.id,
      name: monitor.friendlyName,
      url: monitor.url,
      average: formatNumber(stats.uptime),
      daily,
      total: {
        times: stats.incident_count,
        duration: stats.total_downtime_seconds,
      },
      status: getStatus(monitor.status),
    };
  });

  writeCache(apiToken, historyDays, result);
  return result;
}
